import {
  AlertTriangle,
  Clock3,
  CircleX,
  Expand,
  HardDrive,
  ShieldCheck,
  Wrench,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ErrorState, LoadingBlock } from "../components/ui";
import { ThemeToggle } from "../components/ThemeToggle";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useRemote } from "../hooks/useRemote";
import { apiRequest, queryString } from "../lib/api";
import { formatTime, relativeTime } from "../lib/format";
import {
  readStoredWallboardRefreshInterval,
  storeWallboardRefreshInterval,
  WALLBOARD_REFRESH_INTERVALS,
  type WallboardRefreshInterval,
} from "../lib/wallboard-settings";
import type { BuildingListItem, WallboardResponse } from "../types";

const GRID_SIZES = [5, 6, 7, 8] as const;
type GridSize = (typeof GRID_SIZES)[number];

function hasCriticalFailure(building: BuildingListItem): boolean {
  return building.networkStatus === "outage" || building.recorderStatus === "outage";
}

function failureLabel(building: BuildingListItem): string {
  const networkDown = building.networkStatus === "outage";
  const recorderDown = building.recorderStatus === "outage";
  if (networkDown && recorderDown) return "Sin conexión y DVR fuera de servicio";
  if (networkDown) return "Sin conexión";
  if (recorderDown) return "DVR fuera de servicio";
  if (building.status === "stale") return "Sin datos recientes";
  if (building.status === "maintenance") return "En mantenimiento";
  if (building.status === "degraded") return "Necesita revisión";
  return "Operativo";
}

function cardState(building: BuildingListItem): "alarm" | "stale" | "maintenance" | "attention" | "ok" {
  if (hasCriticalFailure(building)) return "alarm";
  if (building.status === "stale") return "stale";
  if (building.status === "maintenance") return "maintenance";
  if (building.status === "degraded") return "attention";
  return "ok";
}

function signalClass(status: BuildingListItem["networkStatus"]): "is-down" | "is-unknown" | "is-ok" {
  if (status === "outage") return "is-down";
  if (status !== "operational") return "is-unknown";
  return "is-ok";
}

function signalLabel(status: BuildingListItem["networkStatus"], signal: "network" | "recorder"): string {
  const name = signal === "network" ? "Red" : "DVR";
  if (status === "outage") return signal === "network" ? "Sin red" : "DVR caído";
  if (status === "stale") return `${name} sin datos`;
  if (status === "maintenance") return `${name} en mantenimiento`;
  if (status === "degraded") return `${name} por revisar`;
  return name;
}

export function WallboardPage() {
  useDocumentTitle("Modo mural");
  const [gridSize, setGridSize] = useState<GridSize>(7);
  const [refreshIntervalSeconds, setRefreshIntervalSeconds] = useState<WallboardRefreshInterval>(() =>
    readStoredWallboardRefreshInterval(window.localStorage),
  );
  const [rotation, setRotation] = useState<{ region: string; cursor: string | null }>({
    region: "",
    cursor: null,
  });
  const [now, setNow] = useState(new Date());
  const requestPath = `/api/v1/wallboard${queryString({ ...rotation, gridSize })}`;
  const remote = useRemote(() => apiRequest<WallboardResponse>(requestPath), [requestPath]);
  const nextRotationRef = useRef(rotation);
  const currentRotationRef = useRef(rotation);
  const reloadRef = useRef(remote.reload);
  currentRotationRef.current = rotation;
  reloadRef.current = remote.reload;

  useEffect(() => {
    if (remote.data) {
      nextRotationRef.current = {
        region: remote.data.nextRegion,
        cursor: remote.data.nextBuildingCursor,
      };
    }
  }, [remote.data]);

  useEffect(() => {
    const clock = window.setInterval(() => setNow(new Date()), 1_000);
    return () => window.clearInterval(clock);
  }, []);

  useEffect(() => {
    storeWallboardRefreshInterval(window.localStorage, refreshIntervalSeconds);
  }, [refreshIntervalSeconds]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const next = nextRotationRef.current;
      const current = currentRotationRef.current;
      if (next.region === current.region && next.cursor === current.cursor) reloadRef.current();
      else setRotation({ ...next });
    }, refreshIntervalSeconds * 1_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [refreshIntervalSeconds]);

  const enterFullscreen = () => {
    if (!document.fullscreenElement) void document.documentElement.requestFullscreen();
    else void document.exitFullscreen();
  };

  const changeGridSize = (nextGridSize: GridSize) => {
    const initialRotation = { region: "", cursor: null };
    nextRotationRef.current = initialRotation;
    setGridSize(nextGridSize);
    setRotation(initialRotation);
  };

  return (
    <main className="wallboard">
      <header className="wallboard__header">
        <div className="wallboard__brand">
          <span className="brand__mark"><ShieldCheck size={25} /></span>
          <div><strong>Centro Monitoreo</strong><span>Hikdos</span></div>
        </div>
        <div className="wallboard__sync">
          <span className={`live-dot live-dot--${remote.data?.lastSynchronization.status ?? "never"}`} />
          <span>Última sincronización: {relativeTime(remote.data?.lastSynchronization.at)}</span>
        </div>
        <div className="wallboard__clock">
          <strong>{formatTime(now.toISOString())}</strong>
          <span>{new Intl.DateTimeFormat("es-AR", { weekday: "long", day: "2-digit", month: "long" }).format(now)}</span>
        </div>
        <label className="wallboard__setting">
          <span>Cuadrícula</span>
          <select value={gridSize} onChange={(event) => changeGridSize(Number(event.target.value) as GridSize)} aria-label="Tamaño de cuadrícula del mural">
            {GRID_SIZES.map((size) => <option key={size} value={size}>{size} x {size}</option>)}
          </select>
        </label>
        <label className="wallboard__setting">
          <span>Rotación</span>
          <select value={refreshIntervalSeconds} onChange={(event) => setRefreshIntervalSeconds(Number(event.target.value) as WallboardRefreshInterval)} aria-label="Intervalo de rotación del mural" title="Cambia la página del mural; no modifica la sincronización del conector">
            {WALLBOARD_REFRESH_INTERVALS.map((seconds) => <option key={seconds} value={seconds}>{seconds} s</option>)}
          </select>
        </label>
        <ThemeToggle compact />
        <button className="wallboard__action" onClick={enterFullscreen} aria-label="Alternar pantalla completa"><Expand size={19} /></button>
        <Link className="wallboard__action" to="/" aria-label="Salir del modo mural"><X size={21} /></Link>
      </header>

      {remote.loading && !remote.data ? <div className="wallboard__loading"><LoadingBlock rows={7} /></div> : null}
      {remote.error && !remote.data ? <div className="wallboard__loading"><ErrorState error={remote.error} onRetry={remote.reload} /></div> : null}
      {remote.error && remote.data ? <div className="wallboard__error" role="alert">No se pudo actualizar el mural. Se muestran los últimos datos y se reintentará automáticamente.</div> : null}
      {remote.data ? <BuildingMosaic data={remote.data} gridSize={gridSize} refreshIntervalSeconds={refreshIntervalSeconds} refreshing={remote.loading || remote.refreshing} /> : null}
    </main>
  );
}

function BuildingMosaic({
  data,
  gridSize,
  refreshIntervalSeconds,
  refreshing,
}: {
  data: WallboardResponse;
  gridSize: GridSize;
  refreshIntervalSeconds: WallboardRefreshInterval;
  refreshing: boolean;
}) {
  const totalBuildings = Object.values(data.totals).reduce((sum, count) => sum + count, 0);
  const failedVisible = data.buildings.filter(hasCriticalFailure).length;
  const unavailableVisible = data.buildings.filter((building) => cardState(building) !== "alarm" && cardState(building) !== "ok").length;
  const operationalVisible = data.buildings.length - failedVisible - unavailableVisible;

  return (
    <div className={`wallboard-mosaic-shell wallboard-mosaic-shell--${gridSize}`} aria-busy={refreshing}>
      <div className="wallboard-mosaic-summary">
        <div>
          <p>Mosaico por región</p>
          <h1>{data.region}</h1>
        </div>
        <div className="wallboard-mosaic-legend" aria-label="Leyenda del mosaico">
          <span className="wallboard-mosaic-legend__alarm"><AlertTriangle size={15} /> {failedVisible} en alerta en esta vista</span>
          <span className="wallboard-mosaic-legend__ok"><ShieldCheck size={15} /> {operationalVisible} operativos</span>
          {unavailableVisible ? <span><Clock3 size={15} /> {unavailableVisible} sin confirmar</span> : null}
          <span>{data.buildings.length} edificios · cuadrícula {gridSize} × {gridSize}</span>
          <span>{totalBuildings} clientes y edificios</span>
          <span>Rotación cada {refreshIntervalSeconds} segundos</span>
        </div>
      </div>

      <section className="wallboard-mosaic" aria-label={`Mosaico de ${gridSize * gridSize} edificios de ${data.region}`}>
        {data.buildings.map((building) => {
          const state = cardState(building);
          const networkDown = building.networkStatus === "outage";
          const recorderDown = building.recorderStatus === "outage";
          return (
            <Link
              className={`wallboard-building wallboard-building--${state}`}
              key={building.id}
              to={`/edificios/${building.id}`}
              aria-label={`${building.name}: ${failureLabel(building)}`}
            >
              <div className="wallboard-building__topline">
                <span className="wallboard-building__state">
                  {state === "alarm" ? <AlertTriangle size={14} /> : state === "stale" ? <Clock3 size={14} /> : state === "maintenance" ? <Wrench size={14} /> : state === "attention" ? <AlertTriangle size={14} /> : <ShieldCheck size={14} />}
                  {state === "alarm" ? "Alerta" : state === "stale" ? "Sin datos" : state === "maintenance" ? "Mantenimiento" : state === "attention" ? "Revisar" : "Operativo"}
                </span>
                <small>{building.externalId}</small>
              </div>
              <div className="wallboard-building__identity">
                <h2>{building.name}</h2>
                <p>{building.client.name} · {building.region.name}</p>
              </div>
              <div className="wallboard-building__signals">
                <span className={signalClass(building.networkStatus)}>
                  {networkDown ? <WifiOff size={14} /> : <Wifi size={14} />} {signalLabel(building.networkStatus, "network")}
                </span>
                <span className={signalClass(building.recorderStatus)}>
                  {recorderDown ? <CircleX size={14} /> : <HardDrive size={14} />} {signalLabel(building.recorderStatus, "recorder")}
                </span>
              </div>
            </Link>
          );
        })}
      </section>
    </div>
  );
}
